'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { JobCardFormData } from './JobCardWizard';
import { Package, Plus, Trash2, AlertTriangle, Search, CheckCircle, Barcode, X } from 'lucide-react';
import { getParts, Part } from '@/lib/partsStore';

interface PartEntry {
  id: string;
  partNumber: string;
  partName: string;
  qty: number;
  stockAvailable: number;
  unitPrice: number;
}

// Barcode validation: alphanumeric, 4–20 chars, may contain hyphens
const BARCODE_REGEX = /^[A-Z0-9][A-Z0-9\-]{2,18}[A-Z0-9]$/i;

function validateBarcode(value: string): string {
  const v = value.trim().toUpperCase();
  if (!v) return 'Enter a part number or scan a barcode';
  if (v.length < 4) return 'Part number too short (min 4 characters)';
  if (v.length > 20) return 'Part number too long (max 20 characters)';
  if (!BARCODE_REGEX.test(v)) return 'Invalid format — use letters, numbers, and hyphens only';
  return '';
}

export default function Step3PartsUsed() {
  const { setValue } = useFormContext<JobCardFormData>();
  const [catalogueParts, setCatalogueParts] = useState<Part[]>([]);
  const [parts, setParts] = useState<PartEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResult, setSearchResult] = useState<Part | null>(null);
  const [searchError, setSearchError] = useState('');
  const [barcodeError, setBarcodeError] = useState('');
  const [qty, setQty] = useState(1);
  const [isFocused, setIsFocused] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCatalogueParts(getParts());
    // Auto-focus the search field when the step mounts
    const timer = setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSearch = useCallback(() => {
    const term = searchTerm.trim().toUpperCase();
    const validationError = validateBarcode(term);
    if (validationError) {
      setBarcodeError(validationError);
      setSearchError('');
      setSearchResult(null);
      return;
    }
    setBarcodeError('');
    const result = catalogueParts.find((p) => p.partNumber.toUpperCase() === term);
    if (!result) {
      setSearchError(`Part "${term}" not found in catalogue`);
      setSearchResult(null);
    } else {
      setSearchResult(result);
      setSearchError('');
    }
  }, [searchTerm, catalogueParts]);

  // Simulate barcode scanner: rapid input followed by Enter triggers scan animation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    setSearchTerm(val);
    setBarcodeError('');
    setSearchError('');
    setSearchResult(null);

    // Detect barcode scanner paste (length jump > 4 chars at once)
    if (val.length >= 4 && val.length - searchTerm.length > 3) {
      setIsScanning(true);
      setTimeout(() => setIsScanning(false), 800);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === 'Escape') {
      setSearchTerm('');
      setSearchResult(null);
      setBarcodeError('');
      setSearchError('');
    }
  };

  const handleAddPart = () => {
    if (!searchResult) return;
    const term = searchTerm.trim().toUpperCase();
    const existing = parts.find((p) => p.partNumber === term);
    if (existing) {
      setSearchError('This part is already added — adjust the quantity below');
      return;
    }
    const newPart: PartEntry = {
      id: `part-${term}-${Date.now()}`,
      partNumber: term,
      partName: searchResult.name,
      qty,
      stockAvailable: searchResult.stock,
      unitPrice: searchResult.unitPrice,
    };
    const updated = [...parts, newPart];
    setParts(updated);
    setValue('parts', updated);
    setSearchTerm('');
    setSearchResult(null);
    setQty(1);
    setSearchError('');
    setBarcodeError('');
    // Re-focus for next scan
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleRemovePart = (id: string) => {
    const updated = parts.filter((p) => p.id !== id);
    setParts(updated);
    setValue('parts', updated);
  };

  const handleQtyChange = (id: string, newQty: number) => {
    const updated = parts.map((p) => p.id === id ? { ...p, qty: Math.max(1, newQty) } : p);
    setParts(updated);
    setValue('parts', updated);
  };

  const suggestions = searchTerm.length >= 2
    ? catalogueParts.filter((p) =>
        p.partNumber.toUpperCase().includes(searchTerm.toUpperCase()) ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 5)
    : [];

  const inputBorderClass = isScanning
    ? 'border-positive ring-2 ring-positive/30'
    : barcodeError
    ? 'border-danger ring-2 ring-danger/20'
    : searchResult
    ? 'border-positive ring-2 ring-positive/20'
    : isFocused
    ? 'border-primary ring-2 ring-primary/20' :'border-border';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
          <Package size={18} className="text-accent" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Parts Used</h2>
          <p className="text-xs text-muted-foreground">Search by part number, scan a barcode, or type a name — stock levels update on submission</p>
        </div>
      </div>

      {catalogueParts.length === 0 && (
        <div className="p-3 rounded-lg border border-warning/30 bg-warning/5 flex items-start gap-2">
          <AlertTriangle size={14} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Parts catalogue is empty. Go to <strong className="text-foreground">Parts Management</strong> to add parts before using this step.
          </p>
        </div>
      )}

      {/* Part search */}
      <div className="p-4 rounded-xl border border-border" style={{ backgroundColor: 'rgba(37,99,235,0.04)' }}>
        <div className="flex items-center justify-between mb-2">
          <label className="label-field">Search Inventory by Part Number or Name</label>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Barcode size={12} />
            <span>Barcode scanner ready</span>
          </div>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            {/* Scan animation overlay */}
            {isScanning && (
              <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none z-10">
                <div
                  className="absolute inset-x-0 h-0.5 bg-positive/70"
                  style={{ animation: 'scanLine 0.8s ease-in-out' }}
                />
              </div>
            )}

            {/* Left icon: barcode when scanning, search otherwise */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20">
              {isScanning ? (
                <Barcode size={15} className="text-positive animate-pulse" />
              ) : (
                <Search size={15} className={isFocused ? 'text-primary' : 'text-muted-foreground'} />
              )}
            </div>

            <input
              ref={searchRef}
              value={searchTerm}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`input-field pl-9 pr-8 font-mono-data uppercase transition-all duration-150 ${inputBorderClass}`}
              placeholder="e.g. BRK-2291 or scan barcode"
              autoComplete="off"
              spellCheck={false}
            />

            {/* Clear button */}
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setSearchResult(null);
                  setBarcodeError('');
                  setSearchError('');
                  searchRef.current?.focus();
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}

            {/* Autocomplete suggestions */}
            {suggestions.length > 0 && !searchResult && isFocused && (
              <div className="absolute top-full left-0 right-0 z-20 mt-1 rounded-lg border border-border bg-card shadow-lg overflow-hidden">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSearchTerm(s.partNumber);
                      setSearchResult(s);
                      setBarcodeError('');
                      setSearchError('');
                      searchRef.current?.focus();
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary transition-colors"
                  >
                    <Package size={13} className="text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">{s.name}</p>
                      <p className="font-mono-data text-xs text-muted-foreground">{s.partNumber}</p>
                    </div>
                    <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">R {s.unitPrice.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" onClick={handleSearch} className="btn-secondary px-4">
            Lookup
          </button>
        </div>

        {/* Barcode validation error */}
        {barcodeError && (
          <p className="text-xs text-danger mt-2 flex items-center gap-1">
            <AlertTriangle size={11} />
            {barcodeError}
          </p>
        )}

        {/* Scan success flash */}
        {isScanning && (
          <p className="text-xs text-positive mt-2 flex items-center gap-1 animate-pulse">
            <Barcode size={11} />
            Barcode detected — press Enter or click Lookup
          </p>
        )}

        {/* Search result */}
        {searchResult && (
          <div className={`mt-3 p-3 rounded-lg border ${searchResult.stock === 0 ? 'border-danger/30 bg-danger/5' : searchResult.stock < searchResult.minStock ? 'border-warning/30 bg-warning/5' : 'border-positive/30 bg-positive/5'}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-foreground">{searchResult.name}</p>
                <p className="font-mono-data text-xs text-muted-foreground mt-0.5">{searchResult.partNumber}</p>
                <p className="text-xs text-muted-foreground mt-0.5">R {searchResult.unitPrice.toFixed(2)} / unit</p>
                <div className="flex items-center gap-2 mt-2">
                  {searchResult.stock === 0 ? (
                    <span className="badge badge-danger text-xs">
                      <AlertTriangle size={10} /> Out of Stock
                    </span>
                  ) : searchResult.stock < searchResult.minStock ? (
                    <span className="badge badge-warning text-xs">
                      <AlertTriangle size={10} /> Low Stock — {searchResult.stock} remaining
                    </span>
                  ) : (
                    <span className="badge badge-success text-xs">
                      <CheckCircle size={10} /> In Stock — {searchResult.stock} available
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div>
                  <label className="label-field text-xs">Qty</label>
                  <input
                    type="number"
                    min={1}
                    max={searchResult.stock || 999}
                    value={qty}
                    onChange={(e) => setQty(parseInt(e.target.value) || 1)}
                    className="input-field font-mono-data w-16 text-center"
                  />
                </div>
                <div className="pt-5">
                  <button
                    type="button"
                    onClick={handleAddPart}
                    disabled={searchResult.stock === 0}
                    className="btn-primary disabled:opacity-40"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>
              </div>
            </div>
            {searchResult.stock > 0 && qty > searchResult.stock && (
              <p className="text-xs text-danger mt-2 flex items-center gap-1">
                <AlertTriangle size={11} />
                Quantity requested ({qty}) exceeds available stock ({searchResult.stock})
              </p>
            )}
          </div>
        )}

        {searchError && (
          <p className="text-xs text-danger mt-2 flex items-center gap-1">
            <AlertTriangle size={11} />
            {searchError}
          </p>
        )}
      </div>

      {/* Parts list */}
      {parts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed border-border rounded-xl">
          <Package size={28} className="text-muted-foreground mb-2" />
          <p className="text-sm font-semibold text-muted-foreground">No parts added yet</p>
          <p className="text-xs text-muted-foreground mt-1">Search for a part number above and click Add</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-foreground">{parts.length} part{parts.length > 1 ? 's' : ''} added</p>
            <p className="text-xs text-muted-foreground">Stock will be deducted on submission</p>
          </div>
          {parts.map((part) => (
            <div
              key={part.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors duration-150"
              style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
            >
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Package size={15} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{part.partName}</p>
                <p className="font-mono-data text-xs text-muted-foreground">{part.partNumber} · R {part.unitPrice.toFixed(2)}/unit</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {part.stockAvailable < 5 && (
                  <AlertTriangle size={13} className="text-warning" title="Low stock item" />
                )}
                <span className="text-xs text-muted-foreground mr-1">Qty:</span>
                <input
                  type="number"
                  min={1}
                  value={part.qty}
                  onChange={(e) => handleQtyChange(part.id, parseInt(e.target.value) || 1)}
                  className="input-field font-mono-data w-14 text-center py-1 text-xs"
                />
              </div>
              <button
                type="button"
                onClick={() => handleRemovePart(part.id)}
                className="p-1.5 rounded-md text-muted-foreground hover:bg-danger/10 hover:text-danger transition-all duration-150"
                aria-label={`Remove ${part.partName}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {/* Deduction summary */}
          <div className="mt-3 p-3 rounded-lg border border-primary/20 bg-primary/5">
            <p className="text-xs font-semibold text-primary mb-1">Inventory Deduction Preview</p>
            {parts.map((part) => (
              <p key={`preview-${part.id}`} className="text-xs text-muted-foreground font-mono-data">
                {part.partNumber} → {part.stockAvailable} − {part.qty} = {Math.max(0, part.stockAvailable - part.qty)} remaining
                · Total: R {(part.unitPrice * part.qty).toFixed(2)}
              </p>
            ))}
            <p className="text-xs font-semibold text-foreground mt-1.5 pt-1.5 border-t border-primary/20">
              Total Parts Cost: R {parts.reduce((sum, p) => sum + p.unitPrice * p.qty, 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}